import React from 'react';
import { Switch } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

export const manageInputCls =
  'mt-2 block min-h-10 w-full rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-white/10 dark:placeholder:text-zinc-400 dark:focus:ring-white';

export const manageLabelCls = 'block text-sm font-medium text-zinc-700 dark:text-zinc-300';

export const manageSectionTitleCls = 'text-lg font-semibold text-zinc-900 dark:text-zinc-100';

export const manageLinkActionCls =
  'inline-flex min-h-10 items-center gap-2 rounded-md px-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100';

export const manageIconButtonCls =
  'inline-flex size-10 items-center justify-center rounded-md text-zinc-600 ring-1 ring-inset ring-zinc-300 hover:bg-zinc-100 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-300 dark:ring-white/10 dark:hover:bg-zinc-800 dark:hover:text-zinc-100';

export function FieldWrapper({ label, children, className = '' }) {
  return (
    <div className={className}>
      <label className={manageLabelCls}>{label}</label>
      {children}
    </div>
  );
}

export function SwitchField({ checked, onChange, children, className = '' }) {
  return (
    <div className={clsx('flex items-center gap-3', className)}>
      <Switch
        checked={checked}
        onChange={onChange}
        className={clsx(
          'relative inline-flex h-6 w-11 shrink-0 rounded-full border border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:ring-offset-2 dark:focus:ring-white dark:focus:ring-offset-zinc-950',
          checked ? 'bg-zinc-900 dark:bg-white' : 'bg-zinc-200 dark:bg-zinc-700',
        )}
      >
        <span className="sr-only">{children}</span>
        <span
          aria-hidden="true"
          className={clsx(
            'pointer-events-none inline-block size-5 translate-x-0 rounded-full bg-zinc-50 shadow ring-0 transition-transform duration-150 ease-out dark:bg-zinc-950',
            checked && 'translate-x-5',
          )}
        />
      </Switch>
      <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">{children}</span>
    </div>
  );
}

export function DismissibleTag({ children, onClick, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1 rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-700 ring-1 ring-inset ring-zinc-200 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-white/10 dark:hover:bg-zinc-700',
        className,
      )}
    >
      <span>{children}</span>
      <XMarkIcon aria-hidden="true" className="size-3.5" />
    </button>
  );
}

export function Tag({ children, active = false, className = '' }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset',
        active
          ? 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20'
          : 'bg-zinc-100 text-zinc-700 ring-zinc-200 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-white/10',
        className,
      )}
    >
      {children}
    </span>
  );
}

export function ActionButton({
  href,
  onClick,
  icon: Icon,
  children,
  className = '',
  style,
  disabled = false,
}) {
  const content = (
    <>
      {Icon && <Icon aria-hidden="true" className="size-4" />}
      <span>{children}</span>
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        aria-disabled={disabled}
        className={clsx(manageLinkActionCls, disabled && 'pointer-events-none opacity-50', className)}
        style={style}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(manageLinkActionCls, className)}
      style={style}
    >
      {content}
    </button>
  );
}

export function IconButton({
  label,
  onClick,
  icon: Icon,
  className = '',
  style,
  disabled = false,
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={clsx(manageIconButtonCls, className)}
      style={style}
    >
      {Icon && <Icon aria-hidden="true" className="size-4" />}
    </button>
  );
}
